import { useState, useCallback, useEffect } from "react"
import { usePostHog } from "@posthog/react"
import { useQueryClient } from "@tanstack/react-query"

import { identifyPostHogUser, userKeys } from "@src/api/user"

const STORAGE_KEY = "cookie-consent"

type ConsentStatus = "accepted" | "rejected" | null

const getStoredConsent = (): ConsentStatus => {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    if (value === "accepted" || value === "rejected") return value
  } catch {
    // storage blocked: treat as no preference
  }
  return null
}

// Used at PostHog init so returning users are opted in before any route
// loader identifies them. Identifying while opted out is lost, and opting in
// afterwards resets the anonymous id, leaving the session unidentified.
export const hasAcceptedCookies = () => getStoredConsent() === "accepted"

export const useCookieConsent = () => {
  const posthog = usePostHog()
  const queryClient = useQueryClient()
  const [consent, setConsent] = useState<ConsentStatus>(getStoredConsent)

  // Restore PostHog state for returning users who already accepted
  useEffect(() => {
    const stored = getStoredConsent()
    if (stored === "accepted") {
      posthog.opt_in_capturing({ captureEventName: false })
    }
  }, [posthog])

  const accept = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "accepted")
    setConsent("accepted")
    // opt_in_capturing resets the anonymous id, so re-identify the logged-in user
    posthog.opt_in_capturing()
    identifyPostHogUser(queryClient.getQueryData(userKeys.current()))
  }, [posthog, queryClient])

  const reject = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "rejected")
    setConsent("rejected")
    posthog.opt_out_capturing()
  }, [posthog])

  return { consent, accept, reject }
}
